import { useEffect, useRef } from 'react'
import { useReducedMotion } from 'motion/react'

const vertexShaderSource = `
  attribute vec2 a_position;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

const fragmentShaderSource = `
  precision mediump float;

  uniform vec2 u_resolution;
  uniform float u_time;

  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.52;
    for (int i = 0; i < 4; i++) {
      value += amplitude * noise(p);
      p = p * 2.03 + 13.17;
      amplitude *= 0.48;
    }
    return value;
  }

  float field(vec2 uv, vec2 center, vec2 scale, float softness) {
    vec2 delta = (uv - center) / scale;
    return 1.0 - smoothstep(0.08, softness, dot(delta, delta));
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / u_resolution.xy;
    uv.y = 1.0 - uv.y;

    float aspect = u_resolution.x / max(u_resolution.y, 1.0);
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);
    float t = u_time;
    float flow = fbm(p * 1.32 + vec2(t * 0.13, -t * 0.095));
    float detail = fbm(p * 2.55 + vec2(-t * 0.075, t * 0.11));
    vec2 warp = vec2(flow - 0.5, detail - 0.5) * 0.19;
    vec2 wuv = uv + warp;

    vec2 coralCenter = vec2(
      0.10 + sin(t * 0.52) * 0.075,
      0.52 + cos(t * 0.43) * 0.075
    );
    vec2 amberCenter = vec2(
      0.34 + cos(t * 0.39) * 0.08,
      0.64 + sin(t * 0.48) * 0.065
    );
    vec2 violetCenter = vec2(
      0.76 + sin(t * 0.44) * 0.085,
      0.61 + cos(t * 0.34) * 0.075
    );
    vec2 mintCenter = vec2(
      0.61 + cos(t * 0.47) * 0.065,
      0.79 + sin(t * 0.37) * 0.055
    );

    float coral = field(wuv, coralCenter, vec2(0.32, 0.43), 1.15);
    float amber = field(wuv, amberCenter, vec2(0.36, 0.28), 1.18);
    float violet = field(wuv, violetCenter, vec2(0.42, 0.35), 1.2);
    float mint = field(wuv, mintCenter, vec2(0.30, 0.22), 1.12);

    float tideY = 0.54 + sin(wuv.x * 3.4 + t * 0.42) * 0.09;
    float tide = 1.0 - smoothstep(0.03, 0.31, abs(wuv.y - tideY));
    float counterTide = 1.0 - smoothstep(
      0.04,
      0.34,
      abs(wuv.y - (0.69 - sin(wuv.x * 2.7 - t * 0.35) * 0.08))
    );
    coral *= 0.78 + tide * 0.34;
    amber *= 0.74 + counterTide * 0.30;
    violet *= 0.72 + tide * 0.40;
    mint *= 0.72 + counterTide * 0.34;

    vec3 base = vec3(0.965, 0.958, 0.943);
    float paletteA = 0.5 + 0.5 * sin(t * 0.46);
    float paletteB = 0.5 + 0.5 * sin(t * 0.34 + 2.1);
    vec3 coralColor = mix(
      vec3(0.975, 0.245, 0.105),
      vec3(0.93, 0.18, 0.47),
      paletteA * 0.36
    );
    vec3 amberColor = mix(
      vec3(0.985, 0.66, 0.28),
      vec3(0.98, 0.40, 0.19),
      paletteB * 0.38
    );
    vec3 violetColor = mix(
      vec3(0.42, 0.22, 0.94),
      vec3(0.21, 0.44, 0.96),
      paletteA * 0.46
    );
    vec3 mintColor = mix(
      vec3(0.47, 0.92, 0.75),
      vec3(0.20, 0.78, 0.78),
      paletteB * 0.44
    );

    vec3 color = base;
    color = mix(color, coralColor, clamp(coral * 0.94, 0.0, 0.92));
    color = mix(color, amberColor, clamp(amber * 0.49, 0.0, 0.58));
    color = mix(color, violetColor, clamp(violet * 0.82, 0.0, 0.88));
    color = mix(color, mintColor, clamp(mint * 0.56, 0.0, 0.64));

    float sheen = pow(max(0.0, 1.0 - abs(wuv.y - (0.50 + wuv.x * 0.13))), 7.0);
    color = mix(color, vec3(1.0), sheen * 0.18);

    float edgeFade = smoothstep(0.0, 0.11, uv.y) * smoothstep(1.0, 0.82, uv.y);
    float sideFade = smoothstep(0.0, 0.08, uv.x) * smoothstep(1.0, 0.91, uv.x);
    float alpha = clamp((coral + amber + violet + mint) * 0.58, 0.0, 0.96);
    alpha *= edgeFade * sideFade;

    gl_FragColor = vec4(color, alpha);
  }
`

function createShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
) {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

export function HeroShader({ paused }: { paused: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      powerPreference: 'high-performance',
      premultipliedAlpha: true,
    })
    if (!gl) {
      canvas.dataset.fallback = 'true'
      return
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)
    if (!vertexShader || !fragmentShader) {
      canvas.dataset.fallback = 'true'
      return
    }

    const program = gl.createProgram()
    if (!program) return
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      canvas.dataset.fallback = 'true'
      return
    }

    const buffer = gl.createBuffer()
    const positionLocation = gl.getAttribLocation(program, 'a_position')
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution')
    const timeLocation = gl.getUniformLocation(program, 'u_time')
    if (!buffer || positionLocation < 0 || !resolutionLocation || !timeLocation) return

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    )
    gl.useProgram(program)
    gl.enableVertexAttribArray(positionLocation)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

    let frame = 0
    let visible = true
    let pageVisible = document.visibilityState === 'visible'
    const startedAt = performance.now()

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 1.65)
      const width = Math.max(1, Math.round(rect.width * dpr))
      const height = Math.max(1, Math.round(rect.height * dpr))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      gl.viewport(0, 0, width, height)
    }

    const draw = (time: number) => {
      resize()
      gl.useProgram(program)
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height)
      gl.uniform1f(
        timeLocation,
        reducedMotion || paused ? 1.4 : (time - startedAt) / 1000,
      )
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.drawArrays(gl.TRIANGLES, 0, 6)
    }

    const animate = (time: number) => {
      draw(time)
      if (!reducedMotion && !paused && visible && pageVisible) {
        frame = window.requestAnimationFrame(animate)
      }
    }

    const restart = () => {
      window.cancelAnimationFrame(frame)
      if (reducedMotion || paused || !visible || !pageVisible) {
        draw(performance.now())
        return
      }
      frame = window.requestAnimationFrame(animate)
    }

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        restart()
      },
      { rootMargin: '160px 0px' },
    )
    intersectionObserver.observe(canvas)

    const resizeObserver = new ResizeObserver(restart)
    resizeObserver.observe(canvas)

    const handleVisibility = () => {
      pageVisible = document.visibilityState === 'visible'
      restart()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    restart()

    return () => {
      window.cancelAnimationFrame(frame)
      intersectionObserver.disconnect()
      resizeObserver.disconnect()
      document.removeEventListener('visibilitychange', handleVisibility)
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
      gl.deleteShader(vertexShader)
      gl.deleteShader(fragmentShader)
    }
  }, [paused, reducedMotion])

  return (
    <canvas
      aria-hidden="true"
      className="hero-shader-canvas"
      data-motion-state={paused || reducedMotion ? 'paused' : 'running'}
      ref={canvasRef}
    />
  )
}
